import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaCheckCircle } from "react-icons/fa";
import {
  getAssessmentsByStudentId,
  requestDataIssue,
  searchTeacherStudents,
  swapAssessmentStudents,
  transferAssessmentToStudent
} from "../services/APIClient";
import { formatClassDisplayName } from "../utils/classDisplay";
import {
  assertConfirmedMutationResponse,
  assertSubmittedRequestResponse,
  getSupportRequestErrorMessage,
  getSupportRequestReference,
  isDuplicatePendingResponse
} from "../utils/teacherWorkflowRequests";
import "./TeacherSwapResultsModal.css";

const STUDENT_SEARCH_MIN_LENGTH = 2;
const STUDENT_SEARCH_LIMIT = 25;
const STUDENT_SEARCH_DEBOUNCE_MS = 300;

const normaliseName = (name) =>
  String(name || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const getFirstName = (name) => String(name || "This child").split(" ")[0];

const getAssessmentId = (assessment) =>
  assessment?.id ||
  assessment?._id ||
  assessment?.assessmentInstanceId ||
  assessment?.assessment_instance_id;

const getAssessmentName = (assessment) =>
  assessment?.name ||
  assessment?.assessmentName ||
  assessment?.screenerName ||
  assessment?.title ||
  "Screener result";

const getCompletedDate = (assessment) =>
  assessment?.completedAt ||
  assessment?.completed_at ||
  assessment?.dateCompleted ||
  assessment?.date_completed ||
  assessment?.date ||
  assessment?.created_at;

const isToday = (dateValue) => {
  if (!dateValue) {
    return false;
  }

  const parsedDate = new Date(dateValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return false;
  }

  return parsedDate.toDateString() === new Date().toDateString();
};

const normaliseAssessment = (assessment) => ({
  ...assessment,
  id: getAssessmentId(assessment),
  name: getAssessmentName(assessment),
  completedToday:
    assessment?.completedToday ||
    assessment?.completed_today ||
    isToday(getCompletedDate(assessment))
});

const getSwappableResult = (assessments) => {
  const normalisedAssessments = (Array.isArray(assessments) ? assessments : [])
    .map(normaliseAssessment)
    .filter((assessment) => assessment.id);

  return (
    normalisedAssessments.find((assessment) => assessment.completedToday) ||
    normalisedAssessments[0] ||
    null
  );
};

const getAssessmentByName = (assessments, assessmentName) => {
  const normalisedAssessmentName = normaliseName(assessmentName);

  if (!normalisedAssessmentName) {
    return null;
  }

  return (Array.isArray(assessments) ? assessments : []).find(
    (assessment) => normaliseName(assessment.name) === normalisedAssessmentName
  ) || null;
};

const normaliseChild = (child, countryGrades) => ({
  ...child,
  id: child?.id || child?._id || child?.studentid || child?.studentId,
  name: child?.name || child?.fullName || child?.displayName || "Unnamed child",
  classId: child?.classId || child?.classid || child?.class_id,
  className: child?.className || child?.class_name || "Class unavailable",
  classDisplayName: formatClassDisplayName(child, "Class unavailable", countryGrades)
});

const hasMatchingResult = (child, scannedResult) => {
  if (!child || !scannedResult) {
    return false;
  }

  return (child.assessments || []).some(
    (assessment) => normaliseName(assessment.name) === normaliseName(scannedResult.name)
  );
};

const getMatchingResult = (child, scannedResult) => {
  if (!child || !scannedResult) {
    return null;
  }

  return (child.assessments || []).find(
    (assessment) => normaliseName(assessment.name) === normaliseName(scannedResult.name)
  ) || null;
};

const formatResultLabel = (result) => {
  if (!result) {
    return "No result found";
  }

  return result.completedToday
    ? `${result.name}, completed today`
    : `${result.name}, completed`;
};

const formatResultRemovedCopy = (childName, result) => {
  const resultName = result?.name || "selected screener";

  if (result?.completedToday) {
    return `${childName} no longer shows today's ${resultName} result.`;
  }

  return `${childName} no longer shows the ${resultName} result.`;
};

const sortSelectableOptionsFirst = (options) =>
  options
    .map((option, index) => ({ ...option, index }))
    .sort((firstOption, secondOption) => {
      if (firstOption.canSelect !== secondOption.canSelect) {
        return firstOption.canSelect ? -1 : 1;
      }

      return firstOption.index - secondOption.index;
    });

const normalisePrefill = (prefill) => ({
  scannedStudentName: String(prefill?.studentName || "").trim(),
  scannedStudentClassName: String(
    prefill?.studentClassName ||
    prefill?.student_class_name ||
    prefill?.className ||
    prefill?.class_name ||
    ""
  ).trim(),
  intendedStudentName: String(
    prefill?.secondStudentName ||
    prefill?.second_student_name ||
    prefill?.intendedStudentName ||
    prefill?.intended_student_name ||
    ""
  ).trim(),
  intendedStudentClassName: String(
    prefill?.secondStudentClassName ||
    prefill?.second_student_class_name ||
    prefill?.className ||
    prefill?.class_name ||
    ""
  ).trim(),
  className: String(prefill?.className || prefill?.class_name || "").trim(),
  assessmentName: String(prefill?.assessmentName || prefill?.assessment_name || "").trim()
});

const classMatchesPrefill = (child, className) => {
  const normalisedClassName = normaliseName(className);

  if (!normalisedClassName) {
    return true;
  }

  return (
    normaliseName(child.className) === normalisedClassName ||
    normaliseName(child.classDisplayName).includes(normalisedClassName)
  );
};

const findPrefilledChild = (children, childName, className) => {
  const normalisedChildName = normaliseName(childName);

  if (!normalisedChildName) {
    return null;
  }

  return (Array.isArray(children) ? children : []).find(
    (child) =>
      normaliseName(child.name) === normalisedChildName &&
      classMatchesPrefill(child, className)
  ) || null;
};

const withPrefilledResult = (child, assessmentName) => {
  if (!child || !assessmentName) {
    return child;
  }

  const matchingAssessment = getAssessmentByName(child.assessments, assessmentName);

  return matchingAssessment
    ? { ...child, swappableResult: matchingAssessment }
    : null;
};

function TeacherSwapResultsModal({
  show,
  handleClose,
  schoolid,
  countryGrades = null,
  prefill = null,
  onViewStudentResults = null,
  onReturnToResults = null,
  embedded = false,
  onWorkflowComplete = null
}) {
  const [stage, setStage] = useState("form");
  const [scannedSearchTerm, setScannedSearchTerm] = useState("");
  const [intendedSearchTerm, setIntendedSearchTerm] = useState("");
  const [scannedMatches, setScannedMatches] = useState([]);
  const [intendedMatches, setIntendedMatches] = useState([]);
  const [selectedScannedChild, setSelectedScannedChild] = useState(null);
  const [selectedIntendedChild, setSelectedIntendedChild] = useState(null);
  const [isSearchingScanned, setIsSearchingScanned] = useState(false);
  const [isSearchingIntended, setIsSearchingIntended] = useState(false);
  const [isSwapping, setIsSwapping] = useState(false);
  const [error, setError] = useState("");
  const [supportReference, setSupportReference] = useState("");
  const [isDuplicatePending, setIsDuplicatePending] = useState(false);
  const [transferError, setTransferError] = useState("");
  const [swapMode, setSwapMode] = useState("transfer");
  const prefillAppliedRef = useRef({ scanned: false, intended: false });
  const prefillContext = useMemo(() => normalisePrefill(prefill), [prefill]);

  const trimmedScannedSearchTerm = scannedSearchTerm.trim();
  const trimmedIntendedSearchTerm = intendedSearchTerm.trim();
  const selectedScannedResult = selectedScannedChild?.swappableResult || null;
  const canSwap = Boolean(
    selectedScannedChild &&
    selectedIntendedChild &&
    selectedScannedResult &&
    !isSwapping
  );

  const resetForm = useCallback(() => {
    setStage("form");
    setScannedSearchTerm(prefillContext.scannedStudentName);
    setIntendedSearchTerm(prefillContext.intendedStudentName);
    setScannedMatches([]);
    setIntendedMatches([]);
    setSelectedScannedChild(null);
    setSelectedIntendedChild(null);
    setSupportReference("");
    setIsDuplicatePending(false);
    setTransferError("");
    setSwapMode("transfer");
    setError("");
    prefillAppliedRef.current = { scanned: false, intended: false };
  }, [prefillContext.scannedStudentName, prefillContext.intendedStudentName]);

  useEffect(() => {
    if (show) {
      resetForm();
    }
  }, [show, resetForm]);

  const enrichChildrenWithResults = useCallback(async (children) =>
    Promise.all(
      (Array.isArray(children) ? children : [])
        .map((child) => normaliseChild(child, countryGrades))
        .filter((child) => child.id)
        .map(async (child) => {
          try {
            const assessments = await getAssessmentsByStudentId(schoolid, child.id);
            const normalisedAssessments = (Array.isArray(assessments) ? assessments : [])
              .map(normaliseAssessment)
              .filter((assessment) => assessment.id);

            return {
              ...child,
              assessments: normalisedAssessments,
              swappableResult: getSwappableResult(normalisedAssessments)
            };
          } catch (assessmentError) {
            console.error("Error checking child results:", assessmentError);

            return {
              ...child,
              assessments: [],
              swappableResult: null,
              resultCheckFailed: true
            };
          }
        })
    ), [schoolid, countryGrades]);

  useEffect(() => {
    if (!show || trimmedScannedSearchTerm.length < STUDENT_SEARCH_MIN_LENGTH) {
      setScannedMatches([]);
      setSelectedScannedChild(null);
      setSelectedIntendedChild(null);
      return undefined;
    }

    let isCurrentSearch = true;
    setIsSearchingScanned(true);
    setError("");

    const timeoutId = window.setTimeout(() => {
      searchTeacherStudents(trimmedScannedSearchTerm, STUDENT_SEARCH_LIMIT)
        .then(enrichChildrenWithResults)
        .then((children) => {
          if (!isCurrentSearch) {
            return;
          }

          setScannedMatches(children);
          setSelectedScannedChild(null);
          setSelectedIntendedChild(null);
        })
        .catch((searchError) => {
          if (!isCurrentSearch) {
            return;
          }

          console.error("Error searching scanned child:", searchError);
          setScannedMatches([]);
          setError("Child search could not be loaded. Please try again.");
        })
        .finally(() => {
          if (isCurrentSearch) {
            setIsSearchingScanned(false);
          }
        });
    }, STUDENT_SEARCH_DEBOUNCE_MS);

    return () => {
      isCurrentSearch = false;
      window.clearTimeout(timeoutId);
    };
  }, [show, trimmedScannedSearchTerm, enrichChildrenWithResults]);

  useEffect(() => {
    if (!show || trimmedIntendedSearchTerm.length < STUDENT_SEARCH_MIN_LENGTH) {
      setIntendedMatches([]);
      setSelectedIntendedChild(null);
      return undefined;
    }

    let isCurrentSearch = true;
    setIsSearchingIntended(true);
    setError("");

    const timeoutId = window.setTimeout(() => {
      searchTeacherStudents(trimmedIntendedSearchTerm, STUDENT_SEARCH_LIMIT)
        .then(enrichChildrenWithResults)
        .then((children) => {
          if (!isCurrentSearch) {
            return;
          }

          setIntendedMatches(children);
          setSelectedIntendedChild(null);
        })
        .catch((searchError) => {
          if (!isCurrentSearch) {
            return;
          }

          console.error("Error searching intended child:", searchError);
          setIntendedMatches([]);
          setError("Child search could not be loaded. Please try again.");
        })
        .finally(() => {
          if (isCurrentSearch) {
            setIsSearchingIntended(false);
          }
        });
    }, STUDENT_SEARCH_DEBOUNCE_MS);

    return () => {
      isCurrentSearch = false;
      window.clearTimeout(timeoutId);
    };
  }, [show, trimmedIntendedSearchTerm, enrichChildrenWithResults]);

  const getScannedStatus = (child) => {
    if (child.resultCheckFailed) {
      return "Could not check results";
    }

    return formatResultLabel(child.swappableResult);
  };

  const getIntendedStatus = (child) => {
    if (!selectedScannedResult) {
      return "Choose the scanned child first";
    }

    if (child.id === selectedScannedChild?.id) {
      return "Choose a different child";
    }

    if (hasMatchingResult(child, selectedScannedResult)) {
      return `${selectedScannedResult.name} already assigned or completed`;
    }

    if (
      selectedScannedChild?.classId &&
      child.classId &&
      selectedScannedChild.classId !== child.classId
    ) {
      return `Different class, no ${selectedScannedResult.name} result yet`;
    }

    return `No ${selectedScannedResult.name} result yet`;
  };

  const getIntendedSelectionWarning = (child) => {
    if (!child || !selectedScannedResult) {
      return "";
    }

    if (hasMatchingResult(child, selectedScannedResult)) {
      return `${child.name} already has ${selectedScannedResult.name} assigned or completed. You can still continue, but check this is the child who should receive this result.`;
    }

    if (child.resultCheckFailed) {
      return `We could not check existing screeners for ${child.name}. You can still continue, but check this is the right child before swapping.`;
    }

    return "";
  };

  const canSelectIntendedChild = (child) => {
    return canSelectIntendedChildFor(child, selectedScannedChild, selectedScannedResult);
  };

  const canSelectIntendedChildFor = (child, scannedChild, scannedResult) => {
    if (!scannedChild || !scannedResult) {
      return false;
    }

    if (child.id === scannedChild.id) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    if (
      !show ||
      prefillAppliedRef.current.scanned ||
      selectedScannedChild ||
      scannedMatches.length < 1
    ) {
      return;
    }

    const matchedChild = withPrefilledResult(
      findPrefilledChild(
        scannedMatches,
        prefillContext.scannedStudentName,
        prefillContext.scannedStudentClassName
      ),
      prefillContext.assessmentName
    );

    prefillAppliedRef.current.scanned = true;

    if (matchedChild?.swappableResult) {
      setSelectedScannedChild(matchedChild);
      setSelectedIntendedChild(null);
    }
  }, [
    show,
    scannedMatches,
    selectedScannedChild,
    prefillContext.scannedStudentName,
    prefillContext.scannedStudentClassName,
    prefillContext.assessmentName
  ]);

  useEffect(() => {
    if (
      !show ||
      prefillAppliedRef.current.intended ||
      selectedIntendedChild ||
      !selectedScannedChild ||
      !selectedScannedResult ||
      intendedMatches.length < 1
    ) {
      return;
    }

    const matchedChild = findPrefilledChild(
      intendedMatches,
      prefillContext.intendedStudentName,
      prefillContext.intendedStudentClassName
    );

    prefillAppliedRef.current.intended = true;

    if (matchedChild && canSelectIntendedChildFor(matchedChild, selectedScannedChild, selectedScannedResult)) {
      setSelectedIntendedChild(matchedChild);
    }
  }, [
    show,
    intendedMatches,
    selectedIntendedChild,
    selectedScannedChild,
    selectedScannedResult,
    prefillContext.intendedStudentName,
    prefillContext.intendedStudentClassName
  ]);

  const handleScannedChildSelect = (child) => {
    if (!child.swappableResult) {
      return;
    }

    setSelectedScannedChild(child);
    setSelectedIntendedChild(null);
    setError("");
  };

  const handleScannedChildChange = () => {
    setSelectedScannedChild(null);
    setSelectedIntendedChild(null);
    setError("");
  };

  const handleIntendedChildSelect = (child) => {
    if (!canSelectIntendedChild(child)) {
      return;
    }

    setSelectedIntendedChild(child);
    setError("");
  };

  const handleIntendedChildChange = () => {
    setSelectedIntendedChild(null);
    setError("");
  };

  const handleSwapResults = async () => {
    if (!canSwap) {
      return;
    }

    setIsSwapping(true);
    setError("");

    try {
      const intendedMatchingResult = getMatchingResult(selectedIntendedChild, selectedScannedResult);

      if (intendedMatchingResult?.id) {
        const response = await swapAssessmentStudents(
          schoolid,
          selectedScannedResult.id,
          intendedMatchingResult.id
        );
        if (![200, 204].includes(response?.status)) {
          throw new Error("Swap response did not confirm success.");
        }
        setSwapMode("swap");
      } else {
        const response = await transferAssessmentToStudent(
          schoolid,
          selectedScannedResult.id,
          selectedIntendedChild.id
        );
        assertConfirmedMutationResponse(response, ["transferred"]);
        setSwapMode("transfer");
      }
      setStage("success");
    } catch (swapError) {
      console.error("Error swapping child results:", swapError);
      const status = swapError?.response?.status;
      if (status === 409 || status === 422) {
        setTransferError(swapError?.response?.data?.message || "The transfer needs review before it can be applied.");
        setStage("review");
      } else {
        setError("Results could not be swapped. Please try again.");
      }
    } finally {
      setIsSwapping(false);
    }
  };

  const handleRequestReview = async () => {
    if (!schoolid || isSwapping) {
      return;
    }

    setIsSwapping(true);
    setError("");

    const payload = {
      studentId: selectedScannedChild?.id,
      classId: selectedScannedChild?.classId || selectedIntendedChild?.classId,
      issueType: "split_results",
      reviewRoute: "swap-students-results",
      note: [
        `Transfer blocked: ${transferError}`,
        `From ${selectedScannedChild?.name}`,
        `To ${selectedIntendedChild?.name}`,
        `Assessment instance ${selectedScannedResult?.id}`
      ].filter(Boolean).join(" | ")
    };

    try {
      const response = await requestDataIssue(schoolid, payload);
      assertSubmittedRequestResponse(response);
      setSupportReference(getSupportRequestReference(response));
      setIsDuplicatePending(isDuplicatePendingResponse(response));
      setStage("review-success");
    } catch (reviewError) {
      setError(getSupportRequestErrorMessage(reviewError));
    } finally {
      setIsSwapping(false);
    }
  };

  const handleReturnToResults = onReturnToResults || onViewStudentResults;

  const handleDone = () => {
    if (onWorkflowComplete) {
      onWorkflowComplete({
        reference: supportReference || selectedIntendedChild?.id || selectedIntendedChild?.name || "",
        message: "Student result workflow complete."
      });
      return;
    }

    handleClose();
  };

  const handleModalHide = () => {
    if (stage === "success" || stage === "review-success") {
      handleDone();
      return;
    }

    handleClose();
  };

  const renderSearchState = (message) => (
    <p className="swap-results-muted">{message}</p>
  );

  const renderSelectedChildSummary = (type) => {
    const isScanned = type === "scanned";
    const child = isScanned ? selectedScannedChild : selectedIntendedChild;

    if (!child) {
      return null;
    }

    const status = isScanned ? getScannedStatus(child) : getIntendedStatus(child);
    const intendedWarning = isScanned ? "" : getIntendedSelectionWarning(child);

    return (
      <>
        <div className="swap-results-selected-card">
          <span className="swap-results-selected-label">Selected</span>
          <strong>{child.name}</strong>
          <span className="swap-results-result-row">
            <span>Class</span>
            <span>{child.classDisplayName}</span>
          </span>
          <span className="swap-results-result-row">
            <span>Result</span>
            <span>{status}</span>
          </span>
          <Button
            type="button"
            variant="outline-secondary"
            size="sm"
            onClick={isScanned ? handleScannedChildChange : handleIntendedChildChange}
          >
            Change
          </Button>
        </div>
        {intendedWarning && (
          <p className="swap-results-selection-warning">
            {intendedWarning}
          </p>
        )}
      </>
    );
  };

  const renderChildResults = (type) => {
    const isScanned = type === "scanned";
    const selectedChild = isScanned ? selectedScannedChild : selectedIntendedChild;
    const matches = isScanned ? scannedMatches : intendedMatches;
    const isSearching = isScanned ? isSearchingScanned : isSearchingIntended;
    const trimmedTerm = isScanned ? trimmedScannedSearchTerm : trimmedIntendedSearchTerm;

    if (selectedChild) {
      return renderSelectedChildSummary(type);
    }

    if (isSearching) {
      return (
        <div className="swap-results-search-state">
          <Spinner animation="border" size="sm" />
          <span>Searching children...</span>
        </div>
      );
    }

    if (trimmedTerm.length < STUDENT_SEARCH_MIN_LENGTH) {
      return renderSearchState("Start typing the child name.");
    }

    if (matches.length < 1) {
      return (
        <div className="swap-results-empty-result">
          <strong>No matching children found.</strong>
          <span>Try another child name.</span>
        </div>
      );
    }

    const childOptions = matches.map((child) => {
      const isSelected = isScanned
        ? selectedScannedChild?.id === child.id
        : selectedIntendedChild?.id === child.id;
      const status = isScanned ? getScannedStatus(child) : getIntendedStatus(child);
      const canSelect = isScanned ? Boolean(child.swappableResult) : canSelectIntendedChild(child);

      return {
        child,
        canSelect,
        isSelected,
        status
      };
    });

    return sortSelectableOptionsFirst(childOptions).map(({ child, canSelect, isSelected, status }) => {
      return (
        <button
          key={`${type}-${child.id}`}
          type="button"
          className={`swap-results-result${isSelected ? " selected" : ""}${canSelect ? "" : " disabled"}`}
          onClick={() => {
            if (isScanned) {
              handleScannedChildSelect(child);
            } else {
              handleIntendedChildSelect(child);
            }
          }}
          aria-label={`${canSelect ? "Select" : "Unavailable"} ${child.name} from ${child.classDisplayName}. ${status}`}
          aria-pressed={isSelected}
          aria-disabled={!canSelect}
          disabled={!canSelect}
        >
          {!canSelect && (
            <span className="swap-results-availability">Unavailable</span>
          )}
          <strong>{child.name}</strong>
          <span className="swap-results-result-row">
            <span>Class</span>
            <span>{child.classDisplayName}</span>
          </span>
          <span className="swap-results-result-row">
            <span>Result</span>
            <span>{status}</span>
          </span>
        </button>
      );
    });
  };

  const renderSummary = () => {
    if (!selectedScannedChild || !selectedIntendedChild) {
      return null;
    }

    return (
      <div className="swap-results-summary">
        <strong>
          Swap {getFirstName(selectedScannedChild.name)}'s result to{" "}
          {getFirstName(selectedIntendedChild.name)}?
        </strong>
        <span>Scanned: {selectedScannedChild.name}</span>
        <span>Meant to scan: {selectedIntendedChild.name}</span>
      </div>
    );
  };

  const renderForm = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Swap Students Results</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="swap-results-intro">
          Choose the child you scanned and the child you meant to scan.
        </p>
        {error && <Alert variant="danger">{error}</Alert>}

        <Form.Group controlId="teacherSwapScannedChildSearch">
          <Form.Label>Who did you scan?</Form.Label>
          {!selectedScannedChild && (
            <>
              <Form.Text as="div" className="swap-results-field-label">
                Search child
              </Form.Text>
              <Form.Control
                type="search"
                value={scannedSearchTerm}
                onChange={(event) => {
                  setScannedSearchTerm(event.target.value);
                  setError("");
                }}
                aria-label="Search scanned child"
              />
            </>
          )}
        </Form.Group>

        <div className="swap-results-results" aria-label="Matching scanned children">
          {renderChildResults("scanned")}
        </div>

        <Form.Group className="swap-results-section" controlId="teacherSwapIntendedChildSearch">
          <Form.Label>Who did you mean to scan?</Form.Label>
          {!selectedIntendedChild && (
            <>
              <Form.Text as="div" className="swap-results-field-label">
                Search child
              </Form.Text>
              <Form.Control
                type="search"
                value={intendedSearchTerm}
                onChange={(event) => {
                  setIntendedSearchTerm(event.target.value);
                  setError("");
                }}
                aria-label="Search intended child"
              />
            </>
          )}
        </Form.Group>

        <div className="swap-results-results" aria-label="Matching intended children">
          {renderChildResults("intended")}
        </div>

        {renderSummary()}
      </Modal.Body>
      <Modal.Footer>
        <span className="swap-results-footer-note">
          {selectedScannedChild && selectedIntendedChild
            ? `${selectedScannedChild.name} and ${selectedIntendedChild.name} selected.`
            : "Select both children."}
        </span>
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSwapResults} disabled={!canSwap}>
          {isSwapping ? "Swapping..." : "Swap results"}
        </Button>
      </Modal.Footer>
    </>
  );

  const renderSuccess = () => (
    <>
      <Modal.Header closeButton>
        <Modal.Title as="h2">Results have been swapped</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="swap-results-intro">The correction is complete.</p>
        <Alert variant="success" className="swap-results-success">
          <FaCheckCircle aria-hidden="true" />
          <span>
            <strong>The result now appears for {selectedIntendedChild?.name}.</strong>
            {swapMode === "swap" ? (
              <span>The matching result already on {selectedIntendedChild?.name} was swapped back to {selectedScannedChild?.name}.</span>
            ) : (
              <span>
                {formatResultRemovedCopy(selectedScannedChild?.name, selectedScannedResult)}
              </span>
            )}
          </span>
        </Alert>
      </Modal.Body>
      <Modal.Footer>
        <span className="swap-results-footer-note">
          {embedded ? "Done returns to SENSAI." : "Choose where to go next."}
        </span>
        <Button variant="outline-secondary" onClick={resetForm}>
          Swap another
        </Button>
        <Button variant={embedded ? "primary" : "outline-secondary"} onClick={handleDone}>
          {embedded ? "Done" : "Back to Help Centre"}
        </Button>
        {!embedded && handleReturnToResults && (
          <Button
            variant="primary"
            onClick={() => handleReturnToResults()}
          >
            View corrected results
          </Button>
        )}
      </Modal.Footer>
    </>
  );

  return (
    <Modal
      show={show}
      onHide={handleModalHide}
      centered
      className="teacher-swap-results-modal"
      backdrop="static"
    >
      {stage === "success" ? renderSuccess() : stage === "review" ? (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Review Needed</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {error && <Alert variant="danger">{error}</Alert>}
            <Alert variant="warning" className="swap-results-success">
              <span>
                <strong>This result could not be transferred automatically.</strong>
                <span>{transferError}</span>
              </span>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={resetForm}>Back</Button>
            <Button variant="primary" onClick={handleRequestReview} disabled={isSwapping}>
              {isSwapping ? "Submitting..." : "Request review"}
            </Button>
          </Modal.Footer>
        </>
      ) : stage === "review-success" ? (
        <>
          <Modal.Header closeButton>
            <Modal.Title as="h2">Request Submitted</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="success" className="swap-results-success">
              <FaCheckCircle aria-hidden="true" />
              <span>
                <strong>{isDuplicatePending ? "A matching review request is already in progress." : "We sent this transfer for review."}</strong>
                <span>{supportReference ? `Reference: ${supportReference}. ` : ""}No results were changed in this step.</span>
              </span>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="primary" onClick={handleDone}>Done</Button>
          </Modal.Footer>
        </>
      ) : renderForm()}
    </Modal>
  );
}

export default TeacherSwapResultsModal;
